import { useMemo } from "react";
import { Search, X } from "lucide-react";
import { useTranslation } from "react-i18next";

import type {
  PlanningCard,
  PlanningPickIntent,
  PlanningPickup,
} from "../../types/planning";
import { boxNumber, canMove, cardCode } from "../../utils/planningBoard";
import { PartnerColorDot } from "../partners/PartnerColorPicker";
import { partnerStripe } from "../../constants/partnerColors";
import {
  EMPTY,
  LoadStatusPill,
  PlanningCardView,
  onSiteSlot,
  planDate,
} from "./PlanningCardView";

/** A row's partner stripe, drawn as an inset edge so it takes no width. */
const rowStripeStyle = (card: PlanningCard) => {
  const stripe = partnerStripe(card.orderClientPartner);
  return stripe ? { boxShadow: `inset 3px 0 0 ${stripe}` } : undefined;
};

/**
 * The containers waiting to be planned.
 *
 * Everything the office has written down and not yet dispatched — the pile the
 * whole screen exists to work through. It is deliberately the loudest thing on
 * the page after the grid itself: a dispatcher's job here is to empty it, and a
 * pool tucked behind a tab is a pool nobody empties.
 *
 * ── Why three ways of looking at the same list ───────────────────────────────
 *
 * Because forty containers and four hundred are different problems. Cards read
 * best when there are few of them and you are choosing between them on their
 * details; rows read best when there are many and you are scanning for one you
 * already know the name of. The day strip is neither — it is a histogram of
 * where the pressure is, and clicking a bar is how a dispatcher says "just
 * Tuesday's, please" before they start placing.
 *
 * None of it hides anything. Sort, filter and density change what is easiest to
 * see, never what is there: the count above the list always names the whole
 * pile, and reads `matches / total` while something is narrowing it, so a
 * filtered view can never be mistaken for an empty one.
 *
 * ── Where the search actually runs ───────────────────────────────────────────
 *
 * Both here and on the server, and the two are not redundant. The server's copy
 * makes the search *complete* — the pool is a capped page, so a container past
 * the cap can only be found by asking for it. This copy makes it *immediate*,
 * narrowing what is already on screen while the debounce runs. Which means
 * `pool` is a searched page and not the pile, and every statement about the pile
 * has to come from `poolTotal` instead.
 */

type Density = "cards" | "rows";
type Sort = "deadline" | "city" | "client";
/** Load status to show: everything, one of the two stated values, or unstated. */
export type PoolLoadFilter = "all" | "empty" | "full" | "none";
/**
 * Shipping line to show: `null` for every line, `""` for containers with no
 * line set, otherwise the line's name (matched case-insensitively).
 */
export type PoolLineFilter = string | null;

interface Props {
  /**
   * The waiting containers as the server last answered — a **searched, capped
   * page**, not the pile. Anything that means "how many are waiting" must use
   * `poolTotal`.
   */
  pool: PlanningCard[];
  /** How many are waiting in total, ignoring the search and the cap. */
  poolTotal: number;
  days: string[];
  /**
   * Truck name (display name, else plate) by vehicle id, for the "already on a
   * truck" chip.
   *
   * A lookup rather than a plate on each card, because the board already has the
   * fleet and the card already has the id — denormalising the plate onto every
   * pool card would put the same string two hundred times on the wire.
   */
  plates: Map<string, string>;
  /** True when the server sent a capped page rather than the whole pool. */
  truncated: boolean;
  density: Density;
  sort: Sort;
  search: string;
  /** `YYYY-MM-DD`, or null for the whole week. */
  dayFilter: string | null;
  loadFilter: PoolLoadFilter;
  lineFilter: PoolLineFilter;
  picked: PlanningPickup | null;
  busyId: string | null;

  onDensity: (density: Density) => void;
  onSort: (sort: Sort) => void;
  onSearch: (term: string) => void;
  onDayFilter: (day: string | null) => void;
  onLoadFilter: (value: PoolLoadFilter) => void;
  onLineFilter: (value: PoolLineFilter) => void;
  /**
   * Resolves a handler id to a name, for each card's bubble.
   *
   * The pool's own cards arrive populated, but a container dragged *out* of a
   * slot and back into the pool comes from a mutation response that carries the
   * id alone — so the page resolves it from the roster it already holds.
   */
  handlerName?: (id: string | null | undefined) => string;
  onPick: (pickup: PlanningPickup, intent?: PlanningPickIntent) => void;
  onOpen: (card: PlanningCard) => void;
  /** The one card opened out to show its stops, if it is in the pool. */
  mapOpenId: string | null;
  onToggleMap: (card: PlanningCard) => void;
  /**
   * Take a waiting container's release back — the same cross the grid offers.
   *
   * Needed here because a released container can *be* in the pool: taking one
   * off its truck no longer withdraws the release, so a card can wait with
   * "Sent to truck" still on it. Without this the only way back to `created`
   * would be to put it on a truck again first, which is not a step anybody
   * should have to take to undo a decision.
   */
  onUnsend: (card: PlanningCard) => void;
  /** True while running containers are being left out of the pool. */
  hideStarted: boolean;
  onHideStarted: (value: boolean) => void;
}

/** The deadline as one sortable string; undated containers sort last. */
const deadlineKey = (card: PlanningCard) =>
  card.deadline ? `${card.deadline.day} ${card.deadline.time ?? "99:99"}` : "￿";

/** A shipping line as a comparison key — "MSC" and "msc " are one carrier. */
const lineKey = (value: string | null) => (value ?? "").trim().toLowerCase();

/**
 * Whether a card passes the load-status, shipping-line and hide-started filters.
 *
 * `hideStarted` is also applied by the server (see `poolHideStarted` in the
 * board validator) and is repeated here for the same reason the search is: the
 * toggle costs a round trip, and without a local copy the list would sit
 * unchanged for it. The caller is responsible for passing `false` while a search
 * is running, which is the one case where the two copies would otherwise
 * disagree — the server deliberately ignores the toggle then.
 */
function matchesAttributes(
  card: PlanningCard,
  loadFilter: PoolLoadFilter,
  lineFilter: PoolLineFilter,
  hideStarted: boolean,
) {
  if (loadFilter !== "all" && (card.loadStatus ?? "none") !== loadFilter) return false;
  if (lineFilter !== null && lineKey(card.shippingLine) !== lineKey(lineFilter)) return false;
  if (hideStarted && card.started) return false;
  return true;
}

const LOAD_FILTERS: PoolLoadFilter[] = ["all", "empty", "full", "none"];

export function ContainerPool({
  pool,
  poolTotal,
  days,
  plates,
  truncated,
  density,
  sort,
  search,
  dayFilter,
  loadFilter,
  lineFilter,
  picked,
  busyId,
  onDensity,
  onSort,
  onSearch,
  onDayFilter,
  onLoadFilter,
  onLineFilter,
  handlerName,
  onPick,
  onOpen,
  mapOpenId,
  onToggleMap,
  onUnsend,
  hideStarted,
  onHideStarted,
}: Props) {
  const { t, i18n } = useTranslation("calendar");

  /**
   * How many waiting containers are due on each day of the window.
   *
   * Counted off `pool` rather than off the day-filtered view, deliberately: the
   * strip is what a dispatcher uses to *choose* a day, so a strip that already
   * reflected the chosen one would collapse to a single bar the moment it was
   * used.
   *
   * A *search* does narrow it, and that is the right behaviour rather than an
   * oversight — while you are searching, the strip is showing where your matches
   * fall, which is the more useful question. It returns to the whole pile when
   * the search is cleared.
   */
  /**
   * Whether anything is narrowing the pile right now.
   *
   * `hideStarted` is deliberately not one of them, even though it is a filter.
   * It is applied by the *server*, so `poolTotal` already counts what it leaves
   * — the header would read "180 / 180" and invite a dispatcher to wonder what
   * the other zero were. `narrowing` below is the wider question, and the only
   * thing it drives is whether the clear button is worth showing.
   */
  const attributeFiltering = loadFilter !== "all" || lineFilter !== null;
  const filtering = Boolean(search.trim() || dayFilter || attributeFiltering);
  const narrowing = attributeFiltering || hideStarted;

  /**
   * The toggle, as the sidebar should apply it *this render*.
   *
   * False while a search is running, matching the server exactly: a search
   * reaches the whole pool, and a container somebody typed the number of should
   * not be withheld because its run has begun.
   */
  const localHideStarted = hideStarted && !search.trim();

  /**
   * The shipping lines on offer, busiest first, from the page the server sent.
   *
   * Read off the pool rather than a fixed list, so the menu only ever offers a
   * carrier that would match something. The chosen one is kept in the list even
   * when a search has narrowed it away, so the select never shows a blank value
   * for a filter that is still applied.
   */
  const lineOptions = useMemo(() => {
    const byKey = new Map<string, { name: string; count: number }>();
    let unstated = 0;
    for (const card of pool) {
      const key = lineKey(card.shippingLine);
      if (!key) {
        unstated += 1;
        continue;
      }
      const entry = byKey.get(key);
      if (entry) entry.count += 1;
      else byKey.set(key, { name: card.shippingLine!.trim(), count: 1 });
    }
    if (lineFilter && !byKey.has(lineKey(lineFilter))) {
      byKey.set(lineKey(lineFilter), { name: lineFilter, count: 0 });
    }
    const lines = [...byKey.values()].sort(
      (a, b) => b.count - a.count || a.name.localeCompare(b.name),
    );
    return { lines, unstated };
  }, [pool, lineFilter]);

  /**
   * The day strip follows the load and line filters but not the day filter —
   * choosing "full MSC boxes" should show where *those* fall across the week,
   * while choosing a day must not collapse the strip it was chosen from.
   */
  const counts = useMemo(() => {
    const byDay = new Map<string, number>();
    for (const card of pool) {
      if (!matchesAttributes(card, loadFilter, lineFilter, localHideStarted)) continue;
      const day = card.deadline?.day;
      if (day) byDay.set(day, (byDay.get(day) ?? 0) + 1);
    }
    return byDay;
  }, [pool, loadFilter, lineFilter, localHideStarted]);

  const peak = Math.max(1, ...Array.from(counts.values()));

  /**
   * How many running containers are in the page in hand.
   *
   * What the toggle would remove, shown beside it so the choice is not blind.
   * Off the page rather than off the pile, because the pile's figure is a count
   * query the board does not run for this — and it only has to be right enough
   * to answer "is there any point in this toggle", which a capped page does.
   */
  const startedCount = useMemo(
    () => pool.reduce((total, card) => total + (card.started ? 1 : 0), 0),
    [pool],
  );

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();

    /**
     * The search also runs on the server; this repeats it locally.
     *
     * Not redundant — the two do different jobs. The server's copy makes the
     * search *complete*: the pool is capped, so without it a company with six
     * hundred waiting containers could type a container number that exists and
     * be told there are no matches, because that row was never among the two
     * hundred sent. The local copy makes it *immediate*: it narrows what is
     * already on screen during the debounce, so the list answers every keystroke
     * instead of stuttering a third of a second behind the typing.
     */
    const matches = pool.filter((card) => {
      if (dayFilter && card.deadline?.day !== dayFilter) return false;
      if (!matchesAttributes(card, loadFilter, lineFilter, localHideStarted)) return false;
      if (!term) return true;
      // Field for field with the server's search in planningController.getBoard.
      return [
        card.city,
        card.client,
        card.warehouse,
        card.containerNumber,
        card.lineNumber,
        card.orderNumber,
        card.shippingLine,
      ].some((value) => value?.toLowerCase().includes(term));
    });

    const compare: Record<Sort, (a: PlanningCard, b: PlanningCard) => number> = {
      deadline: (a, b) => deadlineKey(a).localeCompare(deadlineKey(b)),
      city: (a, b) => (a.city ?? "").localeCompare(b.city ?? ""),
      client: (a, b) => (a.client ?? "").localeCompare(b.client ?? ""),
    };

    return [...matches].sort(compare[sort]);
  }, [pool, search, dayFilter, loadFilter, lineFilter, localHideStarted, sort]);

  /** Cards grouped under the day they are due, for the card density. */
  const grouped = useMemo(() => {
    const byDay = new Map<string, PlanningCard[]>();
    for (const card of visible) {
      const key = card.deadline?.day ?? "";
      const bucket = byDay.get(key);
      if (bucket) bucket.push(card);
      else byDay.set(key, [card]);
    }
    return Array.from(byDay.entries()).sort(([a], [b]) =>
      // The undated group last, matching the sort — it is the tail of the queue,
      // not the head of it.
      (a || "￿").localeCompare(b || "￿"),
    );
  }, [visible]);

  const dayLabel = (day: string) =>
    new Date(`${day}T00:00:00`).toLocaleDateString(i18n.language, {
      weekday: "long",
      day: "numeric",
      month: "short",
    });

  return (
    <div className="flex h-full min-h-0 flex-col bg-white">
      <div className="border-b border-gray-200 p-3 lg:p-4">
        <h2 className="mb-2 text-[15px] font-semibold text-dark-900">
          {t("planning.pool.title")}
        </h2>

        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
          <input
            value={search}
            onChange={(event) => onSearch(event.target.value)}
            placeholder={t("planning.pool.searchPlaceholder")}
            className="w-full rounded-md border border-gray-200 bg-gray-50 py-2 pl-8 pr-8 text-[13px] focus:border-secondary-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-secondary-500"
          />
          {search && (
            <button
              type="button"
              onClick={() => onSearch("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
              aria-label={t("planning.pool.clearSearch")}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        <div className="mt-2.5 flex gap-2">
          <div className="flex shrink-0 overflow-hidden rounded-md border border-gray-200">
            {(["cards", "rows"] as const).map((mode) => (
              <button
                key={mode}
                type="button"
                onClick={() => onDensity(mode)}
                className={`px-2.5 py-1.5 text-[11.5px] font-semibold transition ${
                  density === mode
                    ? "bg-dark-900 text-white"
                    : "bg-white text-gray-500 hover:bg-gray-50"
                }`}
              >
                {t(`planning.pool.density.${mode}`)}
              </button>
            ))}
          </div>
          <select
            value={sort}
            onChange={(event) => onSort(event.target.value as Sort)}
            className="min-w-0 flex-1 rounded-md border border-gray-200 px-2 py-1.5 text-[11px] text-gray-600 focus:border-secondary-500 focus:outline-none"
          >
            {(["deadline", "city", "client"] as const).map((value) => (
              <option key={value} value={value}>
                {t(`planning.pool.sort.${value}`)}
              </option>
            ))}
          </select>
        </div>

        {/* ── What the box is: load status and shipping line ─────────────── */}
        <div className="mt-2.5 flex overflow-hidden rounded-md border border-gray-200">
          {LOAD_FILTERS.map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => onLoadFilter(value)}
              aria-pressed={loadFilter === value}
              className={`min-w-0 flex-1 truncate border-r border-gray-200 px-1.5 py-1.5 text-[11px] font-semibold transition last:border-r-0 ${
                loadFilter === value
                  ? "bg-dark-900 text-white"
                  : "bg-white text-gray-500 hover:bg-gray-50"
              }`}
            >
              {value === "all" ? t("planning.pool.filters.allLoads") : t(`planning.load.${value}`)}
            </button>
          ))}
        </div>

        <div className="mt-2 flex gap-2">
          <select
            value={lineFilter === null ? "__all" : lineFilter === "" ? "__none" : lineFilter}
            onChange={(event) => {
              const next = event.target.value;
              onLineFilter(next === "__all" ? null : next === "__none" ? "" : next);
            }}
            className={`min-w-0 flex-1 rounded-md border px-2 py-1.5 text-[11px] focus:border-secondary-500 focus:outline-none ${
              lineFilter !== null
                ? "border-secondary-400 bg-secondary-50 font-semibold text-secondary-800"
                : "border-gray-200 text-gray-600"
            }`}
            aria-label={t("planning.pool.filters.shippingLine")}
          >
            <option value="__all">{t("planning.pool.filters.allLines")}</option>
            {lineOptions.lines.map((line) => (
              <option key={lineKey(line.name)} value={line.name}>
                {line.name} ({line.count})
              </option>
            ))}
            <option value="__none">
              {t("planning.pool.filters.noLine")} ({lineOptions.unstated})
            </option>
          </select>
          {narrowing && (
            <button
              type="button"
              onClick={() => {
                onLoadFilter("all");
                onLineFilter(null);
                onHideStarted(false);
              }}
              className="flex shrink-0 items-center gap-1 rounded-md border border-gray-200 px-2 py-1.5 text-[11px] font-semibold text-gray-500 transition hover:bg-gray-50"
            >
              <X className="h-3 w-3" />
              {t("planning.pool.filters.clear")}
            </button>
          )}
        </div>

        {/*
          ── Running work, in or out ──────────────────────────────────────────

          Its own row rather than a fourth control crammed into the one above,
          because it asks a different kind of question. The load and line filters
          narrow the page the server sent; this one changes what the server sends
          — a company that dispatches from the container's own form has a pool
          that is mostly running containers, and hiding them here is what makes
          the cap fill with work that still needs planning.

          Shown as a checkbox rather than a segmented control: it is a state to
          be left on, not a choice between two views, and the count beside it
          says what turning it on would cost. The count is read off the page in
          hand, so it disappears while the toggle is on — there is nothing left
          to count, and a stale figure would be worse than none.
        */}
        <label className="mt-2 flex cursor-pointer items-center gap-2 text-[11px] text-gray-600">
          <input
            type="checkbox"
            checked={hideStarted}
            onChange={(event) => onHideStarted(event.target.checked)}
            className="h-3.5 w-3.5 rounded border-gray-300 text-secondary-600 focus:ring-secondary-500"
          />
          <span className={hideStarted ? "font-semibold text-secondary-800" : ""}>
            {t("planning.pool.filters.hideStarted")}
          </span>
          {!hideStarted && startedCount > 0 && (
            <span className="ml-auto shrink-0 rounded-full bg-amber-50 px-1.5 py-0.5 font-mono text-[10px] font-semibold text-amber-700">
              {startedCount}
            </span>
          )}
        </label>

        {/*
          The one case where the toggle is on and not in force.

          The server drops it while a search is running, so a dispatcher who has
          hidden running work and then types a container number gets that
          container whether or not its run has begun. Said out loud, because a
          checkbox that is ticked and visibly not applying is otherwise a bug.
        */}
        {hideStarted && search.trim() && (
          <p className="mt-1 text-[10px] text-gray-400">
            {t("planning.pool.filters.hideStartedSearching")}
          </p>
        )}

        {/* ── The day strip: where the week's pressure is ─────────────────── */}
        <div className="mt-3 flex items-end gap-1 overflow-x-auto pb-0.5">
          {days.map((day) => {
            const count = counts.get(day) ?? 0;
            const active = dayFilter === day;
            return (
              <button
                key={day}
                type="button"
                onClick={() => onDayFilter(active ? null : day)}
                title={t("planning.pool.dayBarTitle", { count })}
                className="flex min-w-[30px] flex-1 flex-col items-center gap-1"
              >
                <span
                  className={`w-full max-w-[34px] rounded-t transition ${
                    active ? "bg-secondary-600" : "bg-secondary-100"
                  }`}
                  style={{ height: `${14 + Math.round((count / peak) * 48)}px` }}
                />
                <span
                  className={`text-[9.5px] font-semibold ${active ? "text-secondary-700" : "text-gray-500"}`}
                >
                  {new Date(`${day}T00:00:00`)
                    .toLocaleDateString(i18n.language, { weekday: "short" })
                    .slice(0, 2)}
                </span>
                <span
                  className={`font-mono text-[10.5px] ${active ? "font-bold text-secondary-700" : "text-dark-900"}`}
                >
                  {Number(day.slice(8))}
                </span>
              </button>
            );
          })}
        </div>

        <button
          type="button"
          onClick={() => onDayFilter(null)}
          className={`mt-2 w-full rounded-md border py-1.5 text-[11.5px] font-semibold transition ${
            dayFilter === null
              ? "border-dark-900 bg-dark-900 text-white"
              : "border-gray-200 bg-white text-gray-500 hover:bg-gray-50"
          }`}
        >
          {t("planning.pool.wholeWeek")}
        </button>
      </div>

      <div className="flex items-baseline justify-between px-3 pt-2.5 text-[12px] text-gray-500 lg:px-4">
        <span>{t("planning.pool.totalWaiting")}</span>
        {/*
          The pile, and — while something is narrowing it — how much of the pile
          is on screen. `pool.length` alone was the pile only while the search
          was local; once it moved to the server this became the count of
          matches, and the header started reporting zero waiting containers to a
          dispatcher who had simply mistyped.
        */}
        <span className="font-mono">
          {filtering ? `${visible.length} / ${poolTotal}` : poolTotal}
        </span>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-3 pb-4 pt-2 lg:px-4">
        {/*
          Which emptiness this is.

          Asked of the *filters*, not of the list length. With search running
          server-side an unmatched term empties `pool` outright, so keying off
          its length announced "every container has been planned onto a truck"
          to somebody who had typed nonsense — a confident, wrong answer to a
          question nobody asked. A pool is only genuinely empty when nothing is
          narrowing it.
        */}
        {filtering && visible.length === 0 ? (
          <p className="py-8 text-center text-[12.5px] text-gray-400">
            {t("planning.pool.noMatches")}
          </p>
        ) : !filtering && poolTotal === 0 ? (
          <p className="py-8 text-center text-[12.5px] text-gray-400">
            {t("planning.pool.allPlanned")}
          </p>
        ) : visible.length === 0 ? (
          <p className="py-8 text-center text-[12.5px] text-gray-400">
            {t("planning.pool.noMatches")}
          </p>
        ) : density === "cards" ? (
          grouped.map(([day, cards]) => (
            <div key={day || "undated"} className="mb-3">
              <div className="mb-2 flex items-baseline justify-between border-b border-gray-200 pb-1.5">
                <span className="text-[12.5px] font-semibold text-dark-900">
                  {day ? dayLabel(day) : t("planning.pool.noDeadline")}
                </span>
                <span className="font-mono text-[11.5px] text-gray-500">
                  {t("planning.pool.groupCount", { count: cards.length })}
                </span>
              </div>
              <div className="space-y-2">
                {cards.map((card) => (
                  <PlanningCardView
                    key={card.id}
                    card={card}
                    assignedPlate={
                      card.vehicleId ? (plates.get(card.vehicleId) ?? null) : null
                    }
                    context="pool"
                    picked={picked?.card.id === card.id}
                    busy={busyId === card.id}
                    onPick={onPick}
                    onOpen={onOpen}
                    mapOpen={mapOpenId === card.id}
                    onToggleMap={onToggleMap}
                    onUnsend={onUnsend}
                    handlerLabel={handlerName?.(card.handler?.id)}
                  />
                ))}
              </div>
            </div>
          ))
        ) : (
          /*
           * The row density is a table on purpose, not a denser card.
           *
           * At this size a dispatcher is scanning one column at a time — every
           * city, or every deadline — and columns are what make that possible.
           * A list of small cards would put the same four values in four
           * different places on every line.
           */
          <table className="w-full border-collapse">
            <thead>
              <tr>
                {/*
                  `loadingSite` is a rename, not a new column: this column has
                  always held `card.client`, which is the *loading site's*
                  company. Calling it "Client" put the wrong name on it, and the
                  mistake got harder to see once the loading site moved to the
                  Addresses directory — the two are different kinds of record
                  now, so the column was not merely mislabelled, it was pointing
                  at the wrong directory entirely.

                  `client` and `onSite` are the genuinely new ones, carrying the
                  two facts the pool could not show: whose booking it is, and
                  when they expect the truck.
                */}
                {(
                  [
                    "city",
                    "loadingSite",
                    "client",
                    "deadline",
                    "onSite",
                    "number",
                  ] as const
                ).map((key) => (
                  <th
                    key={key}
                    className="sticky top-0 border-b border-gray-200 bg-white px-1.5 py-1.5 text-left text-[10.5px] font-semibold text-gray-500"
                  >
                    {t(`planning.pool.columns.${key}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visible.map((card) => (
                <tr
                  key={card.id}
                  draggable={canMove(card)}
                  onDragStart={(event) => {
                    event.dataTransfer.setData("text/plain", card.id);
                    event.dataTransfer.effectAllowed = "move";
                    onPick({ card, from: "pool" }, "grab");
                  }}
                  onClick={() => onPick({ card, from: "pool" }, "toggle")}
                  className={`cursor-pointer align-top transition ${
                    picked?.card.id === card.id
                      ? "bg-secondary-50"
                      : "hover:bg-gray-50"
                  }`}
                >
                  <td
                    className="border-b border-gray-100 px-1.5 py-2 text-[12px] font-bold text-dark-900"
                    style={rowStripeStyle(card)}
                  >
                    {card.city || "—"}
                  </td>
                  <td className="border-b border-gray-100 px-1.5 py-2">
                    <div className="text-[11px] font-semibold text-secondary-700">
                      {card.client || "—"}
                    </div>
                    {card.warehouse && (
                      <div className="text-[9.5px] text-gray-500">
                        {card.warehouse}
                      </div>
                    )}
                    {/* The same warning the cards carry. No column of its own:
                        four columns is what fits, and this is the rare row
                        rather than a field every row has. */}
                    {card.vehicleId && plates.get(card.vehicleId) && (
                      <div className="text-[9.5px] font-semibold text-amber-700">
                        {t("planning.card.onTruck", {
                          plate: plates.get(card.vehicleId),
                        })}
                      </div>
                    )}
                    {/* What state the waiting box is in, in one line.
                        `underway` wins over `sent` where both are true, because
                        a running container in the pool is the surprising one and
                        the dispatcher needs to see it before they drag it. No
                        buttons here: the rows view is for scanning a long pool,
                        and an action on every line is what it trades away for
                        density. Switch to cards to act. */}
                    {card.status === "completed" ? (
                      /* Said in its own words and its own colour, exactly as the
                         card does it. A finished run reading "Under way 6/6" is
                         the one state a dispatcher acts wrongly on — they try to
                         move it, and the board refuses. */
                      <div className="text-[9.5px] font-bold text-green-700">
                        {t("planning.status.completed")}
                      </div>
                    ) : card.started ? (
                      /* The word *and* the count: the status says where the box
                         is, the fraction says how much of the run is left. The
                         count alone said neither. */
                      <div className="text-[9.5px] font-semibold text-amber-700">
                        {t(`planning.status.${card.status}`, {
                          defaultValue: card.status,
                        })}{" "}
                        <span className="font-mono text-amber-600">
                          {card.progress.done}/{card.progress.total}
                        </span>
                      </div>
                    ) : (
                      card.sent && (
                        <div className="text-[9.5px] font-semibold text-green-700">
                          {t("planning.card.sent")}
                        </div>
                      )
                    )}
                  </td>
                  {/* Whose booking it is. Never folded into the column above:
                      that one is a place, this one is a billing identity, and
                      they come from two different directories. */}
                  <td className="border-b border-gray-100 px-1.5 py-2 text-[11px] text-gray-700">
                    <span className="inline-flex items-center gap-1.5">
                      {card.orderClient && (
                        <PartnerColorDot
                          color={partnerStripe(card.orderClientPartner)}
                        />
                      )}
                      {card.orderClient || EMPTY}
                    </span>
                  </td>
                  <td className="whitespace-nowrap border-b border-gray-100 px-1.5 py-2 font-mono text-[11px] text-orange-700">
                    {/* With the year, like the card — see `planDate`. */}
                    {card.deadline
                      ? `${planDate(card.deadline.day)}${card.deadline.time ? ` ${card.deadline.time}` : ""}`
                      : EMPTY}
                  </td>
                  {/* The client's appointment, beside the deadline it is
                      routinely a different day from. Date and time each stand on
                      their own — see `onSiteSlot`. */}
                  <td className="whitespace-nowrap border-b border-gray-100 px-1.5 py-2 font-mono text-[11px] text-secondary-700">
                    {onSiteSlot(card)}
                  </td>
                  <td className="border-b border-gray-100 px-1.5 py-2">
                    {/* The id, with the box number on hover — a row in a
                        sidebar has height for one of them. */}
                    <div
                      className="font-mono text-[10px] text-gray-400"
                      title={boxNumber(card) ?? undefined}
                    >
                      {cardCode(card)}
                    </div>
                    {/* Status and line under the number rather than as two
                        more columns — the pool is a sidebar, and four is what
                        fits. */}
                    <div className="mt-1">
                      <LoadStatusPill status={card.loadStatus} />
                    </div>
                    {card.shippingLine && (
                      <div className="mt-0.5 truncate text-[9.5px] font-semibold text-gray-600">
                        {card.shippingLine}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/*
          A capped list has to say it is capped.
          Silently showing the first two hundred of six hundred is how a
          dispatcher concludes the rest were planned already. Search reaches the
          whole pool — that is what the server-side copy is for — so the way out
          is named rather than left to be discovered.
        */}
        {truncated && !filtering && visible.length > 0 && (
          <p className="border-t border-gray-100 py-3 text-center text-[11px] text-gray-400">
            {t("planning.pool.truncated")}
          </p>
        )}
      </div>
    </div>
  );
}
